import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Box, Text, Flex } from 'atoms';

/**
 * Common Status Change Confirmation Modal for V2
 * Reusable component for confirming active/inactive status changes
 * @param {Object} props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {string|number} props.entityId - ID of the entity (client, node, etc.)
 * @param {string} props.entityName - Name/type of the entity (e.g., "client", "node")
 * @param {string} props.entityDisplayName - Optional display name to show instead of ID
 * @param {boolean} props.newStatus - New status to change to (true = active, false = inactive)
 * @param {Function} props.onConfirm - Callback when user confirms (onClick OK)
 * @param {Function} props.onCancel - Callback when user cancels (onClick Cancel)
 * @param {boolean} props.loading - Loading state for the confirm action
 */
export default function StatusChangeModal({
	visible,
	entityId,
	entityName = 'item',
	entityDisplayName,
	newStatus,
	onConfirm,
	onCancel,
	loading = false,
}) {
	const statusText = newStatus ? 'Active' : 'Inactive';
	const entityLabel = entityName.charAt(0).toUpperCase() + entityName.slice(1);
	const displayValue = entityDisplayName || entityId;

	return (
		<Modal
			title={
				<Flex align="center" gap="8px">
					<ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '22px' }} />
					<Text fontSize="18px" fontWeight="600" marginLeft="12px">
						Confirm Status Change
					</Text>
				</Flex>
			}
			open={visible}
			onOk={onConfirm}
			onCancel={onCancel}
			okText="OK"
			cancelText="Cancel"
			okButtonProps={{
				style: {
					backgroundColor: '#1890ff',
					borderColor: '#1890ff',
				},
				loading,
			}}
			maskClosable={false}
			centered
		>
			<Box mt={2}>
				<Text fontSize="14px">
					Do you want to change the status of {entityLabel}{' '}
					<strong>{displayValue}</strong> to <strong>{statusText}</strong>?
				</Text>
			</Box>
		</Modal>
	);
}

StatusChangeModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	entityName: PropTypes.string,
	entityDisplayName: PropTypes.string,
	newStatus: PropTypes.bool.isRequired,
	onConfirm: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	loading: PropTypes.bool,
};
